var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useMemo, useRef, useState } from "react";
import { IncidentImpact, IncidentIssue, } from "ee-nestjs-interfaces";
import { jiraTicketKey } from "ee-utils";
import { Dropdown, HelpTooltip, StyledButton, StyledButtonStyle, TextInput, } from "ee-react-components";
import { MultiSelectDropdown } from "../MultiSelectDropdown";
import { SuccessIndicator } from "../SuccessIndicator";
import { dateToInputString } from "../../Util";
import { IMPACT_LABELS, ISSUE_LABELS } from "./IncidentLabels";
/**
 * Create/edit form for one incident: uncontrolled text inputs read through refs
 * on save, state only for the fields that drive rendering (the multi-selects and
 * the error message).
 *
 * The same component serves both modes. Editing is a whole-object submit —
 * the service diffs it and writes only if something moved — so there is no
 * reason for the two modes to diverge beyond which endpoint the values are sent
 * to.
 *
 * Mounted fresh each time the dialog opens (the parent renders it
 * conditionally), so the defaults below are read once at mount and no effect is
 * needed to resync them when `incident` changes.
 */
export var IncidentEditDialog = function (_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j;
    var incident = _a.incident, defaults = _a.defaults, chargers = _a.chargers, powernodes = _a.powernodes, alertSuppressions = _a.alertSuppressions, onClose = _a.onClose, onSubmit = _a.onSubmit, onDelete = _a.onDelete;
    var titleRef = useRef(null);
    var descriptionRef = useRef(null);
    var beginningRef = useRef(null);
    var endRef = useRef(null);
    var opsTicketRef = useRef(null);
    var maintenanceJiraTicketRef = useRef(null);
    var maintenanceIonTicketRef = useRef(null);
    var suppressionUntilRef = useRef(null);
    var suppressionTitlePatternRef = useRef(null);
    var suppressionTagPatternRef = useRef(null);
    var suppressionDescriptionRef = useRef(null);
    // What every field below starts from: the incident being edited, or the
    // caller's prefill when there is none. Only `incident` decides which mode the
    // form is in — a prefill is create-mode values, not an incident.
    var initial = incident !== null && incident !== void 0 ? incident : defaults;
    var _k = useState((_b = initial === null || initial === void 0 ? void 0 : initial.impact) !== null && _b !== void 0 ? _b : []), impact = _k[0], setImpact = _k[1];
    var _l = useState((_c = initial === null || initial === void 0 ? void 0 : initial.issue) !== null && _c !== void 0 ? _c : []), issue = _l[0], setIssue = _l[1];
    var _m = useState((_d = initial === null || initial === void 0 ? void 0 : initial.chargerIds) !== null && _d !== void 0 ? _d : []), chargerIds = _m[0], setChargerIds = _m[1];
    var _o = useState((_e = initial === null || initial === void 0 ? void 0 : initial.powerNodeId) !== null && _e !== void 0 ? _e : undefined), powerNodeId = _o[0], setPowerNodeId = _o[1];
    // On for a new incident: a suppression raised for an incident is expected to
    // cover it, and an operator who moves the end almost never means to leave the
    // suppression behind. An existing incident keeps whatever it was saved with.
    var _p = useState((_f = initial === null || initial === void 0 ? void 0 : initial.syncAlertSuppressionTimes) !== null && _f !== void 0 ? _f : true), syncSuppressions = _p[0], setSyncSuppressions = _p[1];
    // Blank means "open one" on a new incident; on an edit it is a mistake, since
    // the incident already has a ticket.
    var isNewIncident = incident == null;
    var _q = useState(), errorMessage = _q[0], setErrorMessage = _q[1];
    var _r = useState(false), saving = _r[0], setSaving = _r[1];
    // Deleting is a two-step: the button arms the confirmation, the confirmation
    // does the work. There is no undo on the other side of it.
    // The suppression this form edits: the incident's existing one, or none, in
    // which case the fields describe one to raise. Read at mount like every other
    // default here — the dialog is rendered conditionally, so it remounts each
    // time it opens and needs no effect to resync.
    var existingSuppression = alertSuppressions === null || alertSuppressions === void 0 ? void 0 : alertSuppressions[0];
    // Open when there is already a suppression to show. Otherwise off: an
    // incident does not imply one, and the fields stay out of the way until
    // someone asks for them.
    var _s = useState(existingSuppression != null), createSuppression = _s[0], setCreateSuppression = _s[1];
    var _t = useState(false), confirmingDelete = _t[0], setConfirmingDelete = _t[1];
    var _u = useState(false), deleting = _u[0], setDeleting = _u[1];
    // A new incident starts now unless the caller named a window; an existing one
    // keeps the start it was given. Backfilling is normal, so the field stays
    // editable either way.
    var beginningDefault = useMemo(function () {
        return dateToInputString((initial === null || initial === void 0 ? void 0 : initial.beginning) ? new Date(initial.beginning) : new Date());
    }, [initial]);
    var endDefault = useMemo(function () { return ((initial === null || initial === void 0 ? void 0 : initial.end) ? dateToInputString(new Date(initial.end)) : ""); }, [initial]);
    // An existing suppression shows its own expiry; a new one starts from the
    // incident's end, which is where it would land anyway if left blank.
    var suppressionUntilDefault = useMemo(function () {
        return existingSuppression
            ? dateToInputString(new Date(existingSuppression.until))
            : endDefault;
    }, [endDefault, existingSuppression]);
    var impactItems = useMemo(function () {
        return Object.values(IncidentImpact).map(function (value) { return ({
            name: IMPACT_LABELS[value],
            value: value,
        }); });
    }, []);
    var issueItems = useMemo(function () {
        return Object.values(IncidentIssue).map(function (value) { return ({
            name: ISSUE_LABELS[value],
            value: value,
        }); });
    }, []);
    var chargerItems = useMemo(function () {
        return chargers.map(function (c) { return ({
            name: "#".concat(c.friendlyId, " (").concat(c.ocppId, ")"),
            value: c.id,
        }); });
    }, [chargers]);
    var powernodeItems = useMemo(function () { return __spreadArray([
        { name: "None", value: undefined, selectedLabel: "None" }
    ], powernodes.map(function (p) { return ({ name: "".concat(p.id, " (").concat(p.model, ")"), value: p.id }); }), true); }, [powernodes]);
    var displayErrorMessage = useCallback(function (message) {
        setErrorMessage(message);
        setTimeout(function () { return setErrorMessage(undefined); }, 10000);
    }, []);
    var onSave = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var title, description, opsTicket, beginningValue, endValue, opsTicketKey, key, maintJiraTyped, maintJiraKey, beginning, end, suppressionUntil, alertSuppression, values, e_1;
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u;
        return __generator(this, function (_v) {
            switch (_v.label) {
                case 0:
                    title = (_b = (_a = titleRef.current) === null || _a === void 0 ? void 0 : _a.value) === null || _b === void 0 ? void 0 : _b.trim();
                    description = (_d = (_c = descriptionRef.current) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.trim();
                    opsTicket = (_f = (_e = opsTicketRef.current) === null || _e === void 0 ? void 0 : _e.value) === null || _f === void 0 ? void 0 : _f.trim();
                    beginningValue = (_g = beginningRef.current) === null || _g === void 0 ? void 0 : _g.value;
                    endValue = (_h = endRef.current) === null || _h === void 0 ? void 0 : _h.value;
                    if (!title)
                        return [2 /*return*/, displayErrorMessage("Title must be set.")];
                    if (!beginningValue)
                        return [2 /*return*/, displayErrorMessage("Start time must be set.")];
                    if (opsTicket) {
                        key = jiraTicketKey(opsTicket);
                        if (key === null) {
                            return [2 /*return*/, displayErrorMessage("The OPS ticket must be a Jira issue key (OPS-336) or a link to one.")];
                        }
                        opsTicketKey = key;
                    }
                    else if (!isNewIncident) {
                        return [2 /*return*/, displayErrorMessage("An OPS ticket is required.")];
                    }
                    maintJiraTyped = (_k = (_j = maintenanceJiraTicketRef.current) === null || _j === void 0 ? void 0 : _j.value) === null || _k === void 0 ? void 0 : _k.trim();
                    maintJiraKey = maintJiraTyped ? jiraTicketKey(maintJiraTyped) : null;
                    if (maintJiraTyped && maintJiraKey === null) {
                        return [2 /*return*/, displayErrorMessage("The MAINT Jira ticket must be a Jira issue key (MAIN-208) or a link " +
                                "to one.")];
                    }
                    beginning = new Date(beginningValue);
                    end = endValue ? new Date(endValue) : null;
                    if (end && end.getTime() <= beginning.getTime()) {
                        return [2 /*return*/, displayErrorMessage("The end must be after the start.")];
                    }
                    suppressionUntil = (_l = suppressionUntilRef.current) === null || _l === void 0 ? void 0 : _l.value;
                    alertSuppression = createSuppression
                        ? {
                            id: existingSuppression === null || existingSuppression === void 0 ? void 0 : existingSuppression.id,
                            titlePattern: (_o = (_m = suppressionTitlePatternRef.current) === null || _m === void 0 ? void 0 : _m.value) === null || _o === void 0 ? void 0 : _o.trim(),
                            tagPattern: (_q = (_p = suppressionTagPatternRef.current) === null || _p === void 0 ? void 0 : _p.value) === null || _q === void 0 ? void 0 : _q.trim(),
                            description: (_s = (_r = suppressionDescriptionRef.current) === null || _r === void 0 ? void 0 : _r.value) === null || _s === void 0 ? void 0 : _s.trim(),
                            until: suppressionUntil ? new Date(suppressionUntil) : undefined,
                        }
                        : undefined;
                    if (alertSuppression && !suppressionUntil && !endValue) {
                        return [2 /*return*/, displayErrorMessage(syncSuppressions
                                ? "A suppression needs an expiry: set the incident's end, or turn " +
                                    "off 'Move alert suppressions' to give the suppression one of " +
                                    "its own."
                                : "A suppression needs an expiry: set the incident's end, or a " +
                                    "suppression expiry of its own.")];
                    }
                    values = __assign({ title: title, 
                        // Blank clears it, same as the other nullable fields.
                        description: description || null, beginning: beginning, end: end, 
                        // Undefined drops the key from the body — the absence is the request.
                        opsTicket: opsTicketKey, maintenanceJiraTicket: maintJiraKey, maintenanceIonTicket: ((_u = (_t = maintenanceIonTicketRef.current) === null || _t === void 0 ? void 0 : _t.value) === null || _u === void 0 ? void 0 : _u.trim()) || null, impact: impact, issue: issue, chargerIds: chargerIds, powerNodeId: powerNodeId !== null && powerNodeId !== void 0 ? powerNodeId : null, syncAlertSuppressionTimes: syncSuppressions }, (alertSuppression ? { alertSuppression: alertSuppression } : {}));
                    setErrorMessage(undefined);
                    setSaving(true);
                    _v.label = 1;
                case 1:
                    _v.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, onSubmit(values)];
                case 2:
                    _v.sent();
                    onClose();
                    return [3 /*break*/, 4];
                case 3:
                    e_1 = _v.sent();
                    setSaving(false);
                    displayErrorMessage(e_1 instanceof Error ? e_1.message : "Could not save the incident.");
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [
        chargerIds,
        isNewIncident,
        createSuppression,
        displayErrorMessage,
        existingSuppression,
        impact,
        issue,
        onClose,
        onSubmit,
        powerNodeId,
        syncSuppressions,
    ]);
    var onConfirmDelete = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var e_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (onDelete == null)
                        return [2 /*return*/];
                    setErrorMessage(undefined);
                    setDeleting(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, onDelete()];
                case 2:
                    _a.sent();
                    onClose();
                    return [3 /*break*/, 4];
                case 3:
                    e_2 = _a.sent();
                    setDeleting(false);
                    displayErrorMessage(e_2 instanceof Error ? e_2.message : "Could not delete the incident.");
                    return [3 /*break*/, 4];
                case 4: return [2 /*return*/];
            }
        });
    }); }, [displayErrorMessage, onClose, onDelete]);
    return (React.createElement("div", { className: "bg-black-1150 rounded-[20px] p-[32px] flex flex-col gap-y-[32px] min-w-[650px] max-h-[85vh] overflow-y-auto" },
        React.createElement("label", { className: "font-N6 text-white w-full text-left" }, incident ? "Edit Incident" : "Create Incident"),
        React.createElement("div", { className: "grid grid-cols-2 gap-x-4 gap-y-4 items-center" },
            React.createElement("label", { className: "font-N7 text-white" }, "Title:"),
            React.createElement(TextInput, { placeholder: "Short summary", ref: titleRef, defaultValue: initial === null || initial === void 0 ? void 0 : initial.title }),
            React.createElement("div", { className: "col-span-2 flex flex-col gap-y-2" },
                React.createElement("label", { className: "font-N7 text-white" }, "Description:"),
                React.createElement("textarea", { placeholder: "What happened", ref: descriptionRef, defaultValue: (_g = initial === null || initial === void 0 ? void 0 : initial.description) !== null && _g !== void 0 ? _g : undefined, rows: 8, className: "bg-black-1000 hover:bg-black-800 rounded-[10px] px-[24px] py-[16px] font-N8 text-white w-full resize-y min-h-[160px]" })),
            React.createElement("hr", { className: "border-black-800 max-w-[50%] col-span-2 mx-auto w-full" }),
            React.createElement("label", { className: "font-N7 text-white" }, "Start:"),
            React.createElement(TextInput, { type: "datetime-local", ref: beginningRef, defaultValue: beginningDefault }),
            React.createElement("label", { className: "font-N7 text-white" }, "End:"),
            React.createElement("div", { className: "flex flex-row gap-x-2 items-center" },
                React.createElement(TextInput, { type: "datetime-local", ref: endRef, defaultValue: endDefault }),
                React.createElement(HelpTooltip, { text: "Leave blank while the incident is ongoing. Clearing an end reopens a closed incident." })),
            React.createElement("hr", { className: "border-black-800 max-w-[50%] col-span-2 mx-auto w-full" }),
            React.createElement("label", { className: "font-N7 text-white" }, "OPS ticket:"),
            React.createElement("div", { className: "flex flex-row gap-x-2 items-center" },
                React.createElement(TextInput, { placeholder: isNewIncident
                        ? "OPS-336, a link, or blank to open one"
                        : "OPS-336 or a link (required)", ref: opsTicketRef, defaultValue: initial === null || initial === void 0 ? void 0 : initial.opsTicket }),
                React.createElement(HelpTooltip, { text: isNewIncident
                        ? "Paste the Jira link or type the key; only the key (OPS-336) is stored. Leave it blank and an OPS ticket is opened in Jira for this incident, recording you as the operator who declared it and linking back to the incident page."
                        : "Paste the Jira link or type the key; only the key (OPS-336) is stored." })),
            React.createElement("label", { className: "font-N7 text-white" }, "MAINT Jira ticket:"),
            React.createElement(TextInput, { placeholder: "MAIN-208 or a link (optional)", ref: maintenanceJiraTicketRef, defaultValue: (_h = initial === null || initial === void 0 ? void 0 : initial.maintenanceJiraTicket) !== null && _h !== void 0 ? _h : undefined }),
            React.createElement("label", { className: "font-N7 text-white" }, "Maintenance ION ticket:"),
            React.createElement(TextInput, { placeholder: "Optional", ref: maintenanceIonTicketRef, defaultValue: (_j = initial === null || initial === void 0 ? void 0 : initial.maintenanceIonTicket) !== null && _j !== void 0 ? _j : undefined }),
            React.createElement("hr", { className: "border-black-800 max-w-[50%] col-span-2 mx-auto w-full" }),
            React.createElement("label", { className: "font-N7 text-white" }, "Impact:"),
            React.createElement(CheckboxGroup, { items: impactItems, selected: impact, onChange: setImpact }),
            chargers.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement("label", { className: "font-N7 text-white" }, "Chargers:"),
                React.createElement("div", { className: "flex flex-row gap-x-2 items-center" },
                    React.createElement(MultiSelectDropdown, { items: chargerItems, currentSelections: chargerIds, onChange: setChargerIds }),
                    React.createElement(HelpTooltip, { text: "Optional. A site- or BESS-level incident need not name individual chargers." })))),
            React.createElement("label", { className: "font-N7 text-white" }, "Issue:"),
            React.createElement(CheckboxGroup, { items: issueItems, selected: issue, onChange: setIssue }),
            powernodes.length > 0 && (React.createElement(React.Fragment, null,
                React.createElement("label", { className: "font-N7 text-white" }, "PowerNode:"),
                React.createElement(Dropdown, { items: powernodeItems, currentSelection: powerNodeId, onClick: setPowerNodeId }))),
            React.createElement("hr", { className: "border-black-800 max-w-[50%] col-span-2 mx-auto w-full" }),
            React.createElement("label", { className: "font-N7 text-white" }, existingSuppression
                ? "Alert suppression:"
                : "Create alert suppression:"),
            React.createElement("input", { type: "checkbox", className: "w-[20px] h-[20px] accent-green", checked: createSuppression, onChange: function (e) { return setCreateSuppression(e.target.checked); } }),
            createSuppression && (React.createElement("div", { className: "col-span-2 grid grid-cols-2 gap-x-4 gap-y-4 items-center border-l-2 border-black-800 pl-4" },
                React.createElement("label", { className: "font-N7 text-white" }, "Tie to end of incident"),
                React.createElement("input", { type: "checkbox", className: "w-[20px] h-[20px] accent-green", checked: syncSuppressions, onChange: function (e) { return setSyncSuppressions(e.target.checked); } }),
                !syncSuppressions && (React.createElement(React.Fragment, null,
                    React.createElement("label", { className: "font-N7 text-white" }, "Suppress until:"),
                    React.createElement(TextInput, { type: "datetime-local", ref: suppressionUntilRef, defaultValue: suppressionUntilDefault }))),
                React.createElement("label", { className: "font-N7 text-white" }, "Title pattern:"),
                React.createElement(TextInput, { placeholder: "Matched against the alert title", ref: suppressionTitlePatternRef, defaultValue: existingSuppression === null || existingSuppression === void 0 ? void 0 : existingSuppression.titlePattern }),
                React.createElement("label", { className: "font-N7 text-white" }, "Tag pattern:"),
                React.createElement(TextInput, { placeholder: "Matched against the alert tags", ref: suppressionTagPatternRef, defaultValue: existingSuppression === null || existingSuppression === void 0 ? void 0 : existingSuppression.tagPattern }),
                React.createElement("label", { className: "font-N7 text-white" }, "Suppression description:"),
                React.createElement(TextInput, { placeholder: "Defaults to the incident title", ref: suppressionDescriptionRef, defaultValue: existingSuppression === null || existingSuppression === void 0 ? void 0 : existingSuppression.description }))),
            React.createElement("div", { className: "flex flex-row col-span-2 gap-x-4 mt-16" },
                React.createElement(StyledButton, { text: saving ? "SAVING..." : "SUBMIT", style: StyledButtonStyle.Green, disabled: saving, onClick: onSave }),
                React.createElement(StyledButton, { text: "CLOSE", style: StyledButtonStyle.Gray, onClick: onClose }),
                onDelete != null && !confirmingDelete && (React.createElement(StyledButton, { text: "DELETE", style: StyledButtonStyle.Gray, className: "ml-auto", disabled: saving, onClick: function () { return setConfirmingDelete(true); } }))),
            onDelete != null && confirmingDelete && (React.createElement("div", { className: "col-span-2 flex flex-col gap-y-4" },
                React.createElement("label", { className: "font-N8 text-red" }, "Deleting retires this incident: it stops appearing in the console and in every list and report built from it. This cannot be undone."),
                React.createElement("div", { className: "flex flex-row gap-x-4" },
                    React.createElement(StyledButton, { text: deleting ? "DELETING..." : "CONFIRM DELETE", style: StyledButtonStyle.Green, disabled: deleting, onClick: onConfirmDelete }),
                    React.createElement(StyledButton, { text: "CANCEL", style: StyledButtonStyle.Gray, disabled: deleting, onClick: function () { return setConfirmingDelete(false); } }))))),
        React.createElement(SuccessIndicator, { message: errorMessage, success: false })));
};
/**
 * A fixed, short list of options as checkboxes rather than a dropdown: impact
 * and issue are both small closed sets, and picking several is the normal case,
 * so showing every option beats hiding them behind a menu.
 *
 * Selections come back in `items` order rather than click order, so the value
 * an incident stores — and the string the detail page joins from it — does not
 * depend on which box the operator happened to tick first.
 */
var CheckboxGroup = function (_a) {
    var items = _a.items, selected = _a.selected, onChange = _a.onChange;
    return (React.createElement("div", { className: "grid grid-cols-2 gap-x-6 gap-y-2" }, items.map(function (_a) {
        var name = _a.name, value = _a.value;
        return (React.createElement("label", { key: value, className: "flex flex-row gap-x-2 items-center font-N8 text-white cursor-pointer" },
            React.createElement("input", { type: "checkbox", className: "w-[20px] h-[20px] accent-green shrink-0", checked: selected.includes(value), onChange: function (e) {
                    var next = new Set(selected);
                    if (e.target.checked)
                        next.add(value);
                    else
                        next.delete(value);
                    onChange(items.map(function (i) { return i.value; }).filter(function (v) { return next.has(v); }));
                } }),
            name));
    })));
};
